import polyline from '@mapbox/polyline';

/////////////////////////////////////////////////////////////////
// Helpers for the "Dispatched from" section of the product page //
/////////////////////////////////////////////////////////////////

const EARTH_RADIUS_MILES = 3958.8;
const DEG_TO_RAD = Math.PI / 180;

// Colours of the pins and the journey line. They mirror --marketplaceColor
// and --ethyInk, but static map APIs need them as plain hex strings.
const BRAND_HEX = '138041';
const INK_HEX = '1d1d20';

// With only the dispatch pin on the map, zoom out far enough that shoppers can
// see where in the country (or the world) the product comes from.
const SINGLE_PIN_ZOOM = 5;

// Static map APIs cap the image at 640px wide; the images are requested at 2x.
export const STATIC_MAP_MAX_WIDTH = 640;

/**
 * Great-circle ("as the crow flies") distance between two points in miles.
 *
 * @param {{lat: number, lng: number}} from
 * @param {{lat: number, lng: number}} to
 * @returns {number} miles
 */
export const distanceInMiles = (from, to) => {
  const dLat = (to.lat - from.lat) * DEG_TO_RAD;
  const dLng = (to.lng - from.lng) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(from.lat * DEG_TO_RAD) * Math.cos(to.lat * DEG_TO_RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Round a distance for display. The result is only ever an estimate, so we
 * don't pretend to more precision than that: whole miles, and anything under
 * a mile is reported as 1.
 *
 * @param {number} miles
 * @returns {number}
 */
export const roundMiles = miles => Math.max(1, Math.round(miles));

/**
 * A gently curved line between two points, so the journey reads as a route on
 * the map rather than a ruler line. Returned as [lat, lng] pairs.
 *
 * @param {{lat: number, lng: number}} from
 * @param {{lat: number, lng: number}} to
 * @param {number} [steps]
 * @returns {Array<Array<number>>}
 */
export const journeyPath = (from, to, steps = 24) => {
  const midLat = (from.lat + to.lat) / 2;
  const midLng = (from.lng + to.lng) / 2;
  // Bow the line out sideways by a fifth of its length.
  const control = {
    lat: midLat + (to.lng - from.lng) * 0.2,
    lng: midLng - (to.lat - from.lat) * 0.2,
  };

  const points = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const lat = u * u * from.lat + 2 * u * t * control.lat + t * t * to.lat;
    const lng = u * u * from.lng + 2 * u * t * control.lng + t * t * to.lng;
    points.push([Number(lat.toFixed(5)), Number(lng.toFixed(5))]);
  }
  return points;
};

const mapboxUrl = ({ origin, destination, width, height, accessToken }) => {
  const dispatchPin = `pin-l-warehouse+${BRAND_HEX}(${origin.lng},${origin.lat})`;
  const overlays = destination
    ? [
        `path-4+${BRAND_HEX}-0.85(${encodeURIComponent(
          polyline.encode(journeyPath(origin, destination))
        )})`,
        dispatchPin,
        `pin-l-home+${INK_HEX}(${destination.lng},${destination.lat})`,
      ]
    : [dispatchPin];
  const viewport = destination ? 'auto' : `${origin.lng},${origin.lat},${SINGLE_PIN_ZOOM}`;
  const paddingMaybe = destination ? '&padding=60' : '';

  return (
    'https://api.mapbox.com/styles/v1/mapbox/light-v11/static/' +
    `${overlays.join(',')}/${viewport}/${width}x${height}@2x` +
    `?access_token=${accessToken}${paddingMaybe}`
  );
};

const googleMapsUrl = ({ origin, destination, width, height, apiKey }) => {
  const params = new URLSearchParams();
  params.append('size', `${width}x${height}`);
  params.append('scale', '2');
  params.append('maptype', 'roadmap');
  params.append('markers', `color:0x${BRAND_HEX}|label:D|${origin.lat},${origin.lng}`);
  if (destination) {
    params.append('markers', `color:0x${INK_HEX}|label:Y|${destination.lat},${destination.lng}`);
    params.append(
      'path',
      `color:0x${BRAND_HEX}d9|weight:4|enc:${polyline.encode(journeyPath(origin, destination))}`
    );
  } else {
    params.append('center', `${origin.lat},${origin.lng}`);
    params.append('zoom', `${SINGLE_PIN_ZOOM}`);
  }
  params.append('key', apiKey);
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

/**
 * URL of a static map image showing where a product is dispatched from, and —
 * once the shopper has told us where they are — the journey to them.
 *
 * @param {Object} params
 * @param {Object} params.mapsConfig config.maps
 * @param {{lat: number, lng: number}} params.origin dispatch location
 * @param {{lat: number, lng: number}} [params.destination] shopper's location
 * @param {number} params.width image width in CSS pixels
 * @param {number} params.height image height in CSS pixels
 * @returns {string|null} null when the map provider has no credentials
 */
export const staticJourneyMapUrl = ({ mapsConfig, origin, destination, width, height }) => {
  const size = {
    width: Math.min(Math.round(width), STATIC_MAP_MAX_WIDTH),
    height: Math.min(Math.round(height), STATIC_MAP_MAX_WIDTH),
  };
  if (mapsConfig?.mapProvider === 'googleMaps') {
    return mapsConfig.googleMapsAPIKey
      ? googleMapsUrl({ origin, destination, ...size, apiKey: mapsConfig.googleMapsAPIKey })
      : null;
  }
  return mapsConfig?.mapboxAccessToken
    ? mapboxUrl({ origin, destination, ...size, accessToken: mapsConfig.mapboxAccessToken })
    : null;
};
